import { Component, inject, signal, viewChild } from '@angular/core';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  buildWizardStepDependencyMeta,
  DEFAULT_TOASTER_LIFE,
  getWizardStepData,
  ISummaryPillItem,
  IWizardContext,
  persistWizardStepData,
  SelectOption,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import { tap } from 'rxjs';
import { StandDivisionAndFusion } from '../services/stand-division-and-fusion';
import { ISelectPropertyStepData } from '../interfaces/ISelectPropertyStepData';

type SelectValue = string | string[] | null;

@Component({
  selector: 'lib-step-select-establishment-division-and-fusion',
  standalone: true,
  imports: [LocalizationPipe, ZnSingleSelect],
  templateUrl: './step-select-establishment.html',
})
export class StepSelectEstablishment
  implements WizardStepComponent<ISelectPropertyStepData>
{
  private readonly toaster = inject(ToasterService);
  private readonly standDivisionAndFusionService = inject(StandDivisionAndFusion);

  readonly propertySelect = viewChild('propertySelect', { read: ZnSingleSelect });

  readonly localizationKeyPrefix = 'Geographic::StandDivisionAndFusion.StepSelectEstablishment';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;

  readonly propertyOptions = signal<SelectOption<string>[]>([]);
  readonly selectedPropertyId = signal<string | null>(null);

  private readonly properties = new Map<string, string>();

  loadProperties = (params?: ZnSelectQueryParams) => this.standDivisionAndFusionService.loadProperties({
        ...params,
      }).pipe(
        tap(response => {
          response.items.forEach(item => {
            this.properties.set(item.key, item.value);
          });
        }),
      );
      
  onActivate(context: IWizardContext): void {
    const activeStepId = context.activeStepId();
    const previousData: ISelectPropertyStepData | undefined = getWizardStepData(context, activeStepId);

    context.patchStepState(activeStepId, {
      visited: true,
    });
    
    if(previousData) this.loadPreviousData(previousData, context);
  }

  onPropertyChange(value: SelectValue): void {
    this.setSelectedProperty(value);
  }

  canLeave(): boolean {
    if (!this.selectedPropertyId()) {
      this.notifySelectionRequired();
      return false;
    }

    return true;
  }

  canLeaveSync(): boolean {
    return true;
  }

  hasMeaningfulData(): boolean {
    return (
      !!this.selectedPropertyId() ||
      this.hasTypedSearchValue()
    );
  }

  collect(context: IWizardContext): ISelectPropertyStepData {
    const propertyId = this.selectedPropertyId() as string;
    const propertyName = this.properties.get(propertyId) ?? null;

    const data: ISelectPropertyStepData = {
      propertyId: this.selectedPropertyId(),
      propertyName: propertyName,
    };
    persistWizardStepData(context, {
      valid: !!this.selectedPropertyId(),
      visited: true,
      completed: true,
      data: data,
      dirty: true,
      meta: buildWizardStepDependencyMeta(data),
      invalidateStepIds: [2, 3, 4, 5],
    });
    return data;
  }

  buildSummary(data: ISelectPropertyStepData): ISummaryPillItem[] {
    return data.propertyName
      ? [{ label: 'Planning::Entity.Property', value: data.propertyName }]
      : [];
  }

  private loadPreviousData(data: ISelectPropertyStepData, context: IWizardContext): void {
      this.selectedPropertyId.set(data.propertyId)
  
      if (data.propertyId && data.propertyName) {
        this.properties.set(data.propertyId, data.propertyName);
      }
  
      this.propertyOptions.set(
        data.propertyId && data.propertyName
          ? [{ key: data.propertyId, value: data.propertyName }]
          : [],
      );
  
      queueMicrotask(() => {
        this.propertySelect()?.writeValue(data.propertyId);
      });
  
      context.patchStepState(context.activeStepId(), {
        visited: true,
        dirty: true,
        completed: true,
        data: data
      });
    }

  private setSelectedProperty(value: SelectValue): void {
    const propertyId = Array.isArray(value) ? (value[0] ?? null) : value;
    this.selectedPropertyId.set(propertyId ?? null);
  }

  private notifySelectionRequired(): void {
    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandDivisionAndFusion.StepSelectEstablishment.SelectionRequired`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
  }
  private hasTypedSearchValue(): boolean {
    return (
      this.hasSearchValue(this.propertySelect())
    );
  }
  
  private hasSearchValue(select: ZnSingleSelect | undefined): boolean {
    return !!select?.search().trim();
  }
}
